/**
 * Cron presets + timezone helpers shared by the refinement Schedules page
 * and the workflow Schedule dialog (DEV-17470), so both surfaces offer the
 * same cadences and the same zone list.
 */

/**
 * Small set of high-utility presets. Order: most-frequent first so the fast
 * cadences (which people set up most often during dev) are easiest to
 * click. Times are local to the chosen timezone, not UTC.
 */
export const cronPresets: { label: string; expr: string }[] = [
  { label: 'Every 5 min', expr: '*/5 * * * *' },
  { label: 'Every 15 min', expr: '*/15 * * * *' },
  { label: 'Every 30 min', expr: '*/30 * * * *' },
  { label: 'Hourly', expr: '0 * * * *' },
  { label: 'Every 6 hours', expr: '0 */6 * * *' },
  { label: 'Daily 09:00', expr: '0 9 * * *' },
  { label: 'Daily midnight', expr: '0 0 * * *' },
  { label: 'Weekly Mon 09:00', expr: '0 9 * * 1' },
];

/** Preset label for an expression, or null when it is a custom cron. */
export function cronPresetLabel(expr: string): string | null {
  const normalized = expr.trim().replace(/\s+/g, ' ');
  return cronPresets.find((p) => p.expr === normalized)?.label ?? null;
}

/**
 * Modern browsers / Node 18+ expose Intl.supportedValuesOf('timeZone'), the
 * canonical IANA list (~600 entries). Fall back to a small curated set for
 * older runtimes so the dropdown is never empty.
 */
export function getTimezones(): string[] {
  try {
    const fn = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] })
      .supportedValuesOf;
    if (typeof fn === 'function') return fn('timeZone');
  } catch {
    // fall through
  }
  return [
    'UTC',
    'America/Los_Angeles',
    'America/Denver',
    'America/Chicago',
    'America/New_York',
    'America/Sao_Paulo',
    'Europe/London',
    'Europe/Paris',
    'Europe/Berlin',
    'Asia/Dubai',
    'Asia/Kolkata',
    'Asia/Singapore',
    'Asia/Tokyo',
    'Australia/Sydney',
  ];
}

export const timezones = getTimezones();

export function getBrowserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}
